import { animate, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CmsComponent, TranslationService } from '@spartacus/core';
import { BehaviorSubject, combineLatest, isObservable, Observable, of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { GgAiTroubleshootService } from '../../services/gg-ai-troubleshoot.service';

export interface GGAiTroubleshootComponent extends CmsComponent {
  title: string;
}

export interface GGSapIdWSDTO {
  sapId: string;
  mainCategoryName: string;
  modelMark: string;
}

export interface GGTopFaq {
  id: string;
  question: string;
  answer: string;
  extractedProblem: string;
  polishedQuestion: string;
  polishedAnswer: string;
  similarityScore: number;
  similarityLabel: string;
}

export interface GGFaqsAI {
  question: string;
  extractedQuestion: string;
  productCode: string;
  topFaqs: GGTopFaq[];
}

interface BaseMessage {
  from: 'user' | 'bot';
}

interface TextMessage extends BaseMessage {
  type: 'text';
  text$: Observable<string>;
}

interface FaqMessage extends BaseMessage {
  type: 'faq';
  title: string;
  status$: Observable<string>;
  statusEnum: string;
  text: string;
  expanded?: boolean;
}

type ChatMessage = TextMessage | FaqMessage;

@Component({
  selector: 'gg-ai-troubleshoot',
  templateUrl: 'gg-ai-troubleshoot.component.html',
  styleUrls: ['gg-ai-troubleshoot.component.scss'],
  standalone: false,
  animations: [
    trigger('fadeOut', [
      transition(':leave', [
        animate('400ms ease-out', style({ opacity: 0 })),
      ]),
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('400ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(1rem)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
})
export class GGAiTroubleshootComponent implements OnDestroy {
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
  private subscriptions = new Subscription();

  applianceForm: FormGroup = this.fb.group({
    text: ['', [Validators.required]],
  });

  issueForm: FormGroup = this.fb.group({
    text: ['', [Validators.required]],
  });

  isChatActive$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isApplianceFormActive$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  isTryAgainApplianceForm: boolean = false;
  isTryAgainApplianceForm$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isIssueResolved: boolean = false;
  isIssueResolved$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  isBotTyping$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  areMessagesLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  sapId!: string;
  mainCategoryName!: string;
  modelMark!: string;
  auidOrSn!: string;
  isApplianceFound: boolean = false;
  isIssueDescriptionForm$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  messages$: BehaviorSubject<ChatMessage[]> = new BehaviorSubject<ChatMessage[]>([]);
  tempMessages: ChatMessage[] = [];

  popoverTriggers: string = 'hover click mousedown touchstart';

  constructor(
    private fb: FormBuilder,
    private ggAiTroubleshootService: GgAiTroubleshootService,
    private translation: TranslationService
  ) {}

  onApplianceFormSubmit(event: Event) {
    event.preventDefault();
    if (this.applianceForm.valid) {
      let formValue: string = this.applianceForm.get('text')?.value;
      this.auidOrSn = formValue.trim();
      this.applianceForm.reset();
      this.isApplianceFormActive$.next(false);
    } else {
      this.applianceForm.markAllAsTouched();
      this.applianceForm.updateValueAndValidity();
    }
  }

  onTryAgainApplianceFormSubmit(event: Event) {
    event.preventDefault();
    if (this.applianceForm.valid) {
      let formValue: string = this.applianceForm.get('text')?.value;
      this.auidOrSn = formValue.trim();
      this.applianceForm.reset();
      this.tempMessages.push(this.getTextMessage(of(this.auidOrSn), 'user'));
      this.showMessages();
      this.isBotTyping$.next(true);
      this.subscriptions.add(
        this.ggAiTroubleshootService.getSapIdFromAuidOrSn(this.auidOrSn).subscribe({
          next: (ggSapIdWSDTO) => {
            if (ggSapIdWSDTO && ggSapIdWSDTO.sapId) {
              if (ggSapIdWSDTO.mainCategoryName) {
                this.mainCategoryName = ggSapIdWSDTO.mainCategoryName;
              }
              if (ggSapIdWSDTO.modelMark) {
                this.modelMark = ggSapIdWSDTO.modelMark;
              }
              this.isApplianceFound = true;
              this.sapId = ggSapIdWSDTO.sapId.replace(/^0+/, '');
              this.isTryAgainApplianceForm$.next(false);
            } else {
              this.isTryAgainApplianceForm$.next(true);
              this.isApplianceFound = false;
            }
            this.addFindDeviceResponseToTempMessages();
            this.showMessages();
          },
          error: () => {
            this.isTryAgainApplianceForm$.next(true);
            this.addErrorMessageToTempMessages();
            this.showMessages();
          },
        })
      );
    } else {
      this.applianceForm.markAllAsTouched();
      this.applianceForm.updateValueAndValidity();
    }
  }

  onIssueFormSubmit(event: Event) {
    event.preventDefault();

    if (this.issueForm.valid) {
      let formValue: string = this.issueForm.get('text')?.value;
      let issueDescription: string = formValue.trim();
      this.issueForm.reset();
      this.tempMessages.push(this.getTextMessage(of(issueDescription), 'user'));
      this.showMessages();
      this.isBotTyping$.next(true);
      this.subscriptions.add(
        this.ggAiTroubleshootService.getFaqsAI(this.sapId, issueDescription).subscribe({
          next: (faqs) => {
            if (faqs && faqs.topFaqs && faqs.topFaqs.length > 0) {
              this.isIssueDescriptionForm$.next(false);
              this.isTryAgainApplianceForm = false;
              this.isIssueResolved = true;
              this.tempMessages.push(this.getTextMessage(this.getTranslation('aiTroubleshoot.solutionFound'), 'bot'));
              faqs.topFaqs.forEach((faq) => {
                this.tempMessages.push(this.getFAQMessage(faq));
              });
              this.tempMessages.push(
                this.getTextMessage(this.getTranslation('aiTroubleshoot.fixedSolution'), 'bot'),
                this.getTextMessage(this.getTranslation('aiTroubleshoot.notFixedSolution'), 'bot')
              );
            } else {
              this.tempMessages.push(
                this.getTextMessage(this.getTranslation('aiTroubleshoot.solutionNotFound'), 'bot')
              );
              this.isIssueResolved = false;

              // uncomment for findAppliance step after no faq's are found

              // this.auidOrSn = '';
              // this.isTryAgainApplianceForm = true;
              // this.sapId = '';
              // this.mainCategoryName = '';
              // this.modelMark = '';
              // this.isIssueDescriptionForm$.next(false);
              // this.isApplianceFound = false;
            }
            this.showMessages();
          },
          error: () => {
            this.auidOrSn = '';
            this.isTryAgainApplianceForm = true;
            this.sapId = '';
            this.mainCategoryName = '';
            this.modelMark = '';
            this.isIssueDescriptionForm$.next(false);
            this.isIssueResolved = false;
            this.isApplianceFound = false;
            this.addErrorMessageToTempMessages();
            this.showMessages();
          },
        })
      );
    } else {
      this.issueForm.markAllAsTouched();
      this.issueForm.updateValueAndValidity();
    }
  }

  showMessages() {
    this.isBotTyping$.next(false);
    this.toggleMessagesLoaded(false);
    this.tempMessages.forEach((msg, i) => {
      setTimeout(() => {
        this.messages$.next([...this.messages$.value, msg]);
        setTimeout(() => this.scrollToBottom(), 0);

        if (i === this.tempMessages.length - 1) {
          this.tempMessages = [];
          this.toggleMessagesLoaded(true);
        }
      }, i * 1000);
    });
  }

  toggleMessagesLoaded(areLoaded: boolean) {
    this.areMessagesLoaded$.next(areLoaded);
  }

  toggleFaq(msg: ChatMessage) {
    if (msg.type === 'faq') {
      msg.expanded = !msg.expanded;
    }
  }

  onChatFadeInDone(event: any) {
    this.isBotTyping$.next(true);
    if (event.toState !== 'void') {
      this.subscriptions.add(
        this.ggAiTroubleshootService.getSapIdFromAuidOrSn(this.auidOrSn).subscribe({
          next: (ggSapIdWSDTO) => {
            if (ggSapIdWSDTO && ggSapIdWSDTO.sapId) {
              if (ggSapIdWSDTO.mainCategoryName) {
                this.mainCategoryName = ggSapIdWSDTO.mainCategoryName;
              }
              if (ggSapIdWSDTO.modelMark) {
                this.modelMark = ggSapIdWSDTO.modelMark;
              }
              this.isApplianceFound = true;
              this.sapId = ggSapIdWSDTO.sapId.replace(/^0+/, '');
              this.isTryAgainApplianceForm$.next(false);
              this.isIssueDescriptionForm$.next(true);
            } else {
              this.isTryAgainApplianceForm$.next(true);
              this.sapId = '';
              this.mainCategoryName = '';
              this.modelMark = '';
              this.isIssueDescriptionForm$.next(false);
              this.isApplianceFound = false;
            }
            this.addFindDeviceResponseToTempMessages();
            this.showMessages();
          },
          error: () => {
            this.isTryAgainApplianceForm$.next(true);
            this.addErrorMessageToTempMessages();
            this.showMessages();
          },
        })
      );
    }
  }

  tryAgainIssueDescriptionSubmit() {
    this.isIssueResolved$.next(false);
  }

  onInitialApplianceFadeOutDone(event: any) {
    if (event.toState === 'void') {
      this.isChatActive$.next(true);
    }
  }

  onTryAgainApplianceFormFadeOutDone(event: any) {
    if (event.toState === 'void') {
      this.isIssueDescriptionForm$.next(true);
    }
  }

  issueDescriptionFormFadeOutDone(event: any) {
    if (event.toState === 'void') {
      this.isTryAgainApplianceForm$.next(this.isTryAgainApplianceForm);
      this.isIssueResolved$.next(this.isIssueResolved);
    }
  }

  issueResolvedFadeOutDone(event) {
    if (event.toState === 'void') {
      this.isIssueDescriptionForm$.next(true);
    }
  }

  getTranslation(key: string): Observable<string> {
    return this.translation.translate(key);
  }

  replaceVariableInText(variableName: string, value: any, text$: Observable<string>): Observable<string> {
    if (isObservable(value)) {
      return combineLatest([text$, value as Observable<string>]).pipe(
        map(([text, resolvedValue]) => text.replace(variableName, resolvedValue))
      );
    } else {
      return text$.pipe(map((text) => text.replace(variableName, value)));
    }
  }

  addFindDeviceResponseToTempMessages() {
    if (this.messages$.value.length == 0) {
      this.tempMessages.push(this.getTextMessage(this.getTranslation('aiTroubleshoot.greeting'), 'bot'));
    }

    if (this.isApplianceFound) {
      let deviceFoundMsg = this.getTranslation('aiTroubleshoot.productFound');
      if (this.mainCategoryName) {
        deviceFoundMsg = this.replaceVariableInText('{mainCategory}', this.mainCategoryName, deviceFoundMsg);
      } else {
        deviceFoundMsg = this.replaceVariableInText('{mainCategory}', '', deviceFoundMsg);
        deviceFoundMsg = this.replaceVariableInText(',', '', deviceFoundMsg);
      }

      if (this.modelMark) {
        deviceFoundMsg = this.replaceVariableInText(
          '{modelOrCode}',
          this.getTranslation('aiTroubleshoot.model'),
          deviceFoundMsg
        );
        deviceFoundMsg = this.replaceVariableInText('{modelMarkOrCode}', this.modelMark, deviceFoundMsg);
      } else {
        deviceFoundMsg = this.replaceVariableInText(
          '{modelOrCode}',
          this.getTranslation('aiTroubleshoot.code'),
          deviceFoundMsg
        );
        deviceFoundMsg = this.replaceVariableInText('{modelMarkOrCode}', this.sapId, deviceFoundMsg);
      }
      this.tempMessages.push(
        this.getTextMessage(deviceFoundMsg, 'bot'),
        this.getTextMessage(this.getTranslation('aiTroubleshoot.describeIssue'), 'bot')
      );
    } else {
      this.tempMessages.push(
        this.getTextMessage(this.getTranslation('aiTroubleshoot.productNotFound'), 'bot'),
        this.getTextMessage(this.getTranslation('aiTroubleshoot.whereToFind'), 'bot')
      );
    }
  }

  addErrorMessageToTempMessages() {
    this.tempMessages.push(this.getTextMessage(this.getTranslation('aiTroubleshoot.error'), 'bot'));
  }

  getTextMessage(text$: Observable<string>, from: 'bot' | 'user'): TextMessage {
    return {
      text$: text$,
      from: from,
      type: 'text',
    };
  }

  getFAQMessage(faq: GGTopFaq): FaqMessage {
    return {
      type: 'faq',
      from: 'bot',
      title: faq.polishedQuestion,
      status$: this.getStatus(faq.similarityLabel),
      statusEnum: faq.similarityLabel,
      text: faq.polishedAnswer,
    };
  }

  getStatus(status: string): Observable<string> {
    switch (status) {
      case 'best':
        return this.getTranslation('aiTroubleshoot.rating_best');
      case 'good':
        return this.getTranslation('aiTroubleshoot.rating_good');
      case 'partial':
        return this.getTranslation('aiTroubleshoot.rating_partial');
      case 'low':
        return this.getTranslation('aiTroubleshoot.rating_low');
      default:
        return of('');
    }
  }

  scrollToBottom(): void {
    try {
      this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
    } catch (err) {
      console.error(err);
    }
  }

  resetToFirstStep() {
    this.isChatActive$.next(false);
  }

  isChatFadeOutDone(event) {
    if (event.toState === 'void') {
      this.applianceForm.reset();
      this.issueForm.reset();
      this.isApplianceFormActive$.next(true);
      this.isTryAgainApplianceForm = false;
      this.isTryAgainApplianceForm$.next(false);
      this.isIssueDescriptionForm$.next(false);
      this.isIssueResolved = false;
      this.isIssueResolved$.next(false);
      this.isBotTyping$.next(false);
      this.areMessagesLoaded$.next(true);
      this.isChatActive$.next(false);
      this.sapId = '';
      this.mainCategoryName = '';
      this.modelMark = '';
      this.isApplianceFound = false;
      this.auidOrSn = '';
      this.messages$ = new BehaviorSubject<ChatMessage[]>([]);
      this.tempMessages = [];
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.areMessagesLoaded$.complete();
    this.messages$.complete();
    this.isChatActive$.complete();
    this.isIssueResolved$.complete();
    this.isApplianceFormActive$.complete();
    this.isTryAgainApplianceForm$.complete();
    this.isIssueDescriptionForm$.complete();
    this.isBotTyping$.complete();
  }
}
